import { useMemo } from 'react';
import { createFileRoute, useNavigate, useSearch, Link } from '@tanstack/react-router';
import { PageHeader } from '../../../components/layout/PageHeader';
import { Button } from '../../../components/ui/Button';
import { defineListPage, useListPage } from '@repo/list';
import type { PaginatedQueryFn } from '@repo/list';
import { DataTable, createUrlAdapter } from '@repo/web-list';
import { usePageTitle } from '../../../lib/hooks';
import { agentClient } from '../../../features/agents/network/agents.api';
import type { AgentListItem, AgentStatus } from '../../../features/agents/types';

// ── Config ──────────────────────────────────────────────────────────────────

const AGENT_STATUS_OPTIONS = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Suspended', value: 'SUSPENDED' },
  { label: 'Terminated', value: 'TERMINATED' },
];

const agentsConfig = defineListPage<AgentListItem>({
  variant: 'data-table',
  resource: 'agents',
  searchable: true,
  columns: [
    {
      id: 'agentNumber',
      header: 'Code',
      accessor: 'agentNumber',
      renderType: 'code',
    },
    {
      id: 'fullName',
      header: 'Name',
      accessor: 'fullName',
      sortable: true,
    },
    {
      id: 'mobile',
      header: 'Mobile',
      accessor: 'mobile',
      renderType: 'phone',
    },
    {
      id: 'primaryOrgUnitId',
      header: 'Territory',
      accessor: 'primaryOrgUnitId',
      renderType: 'text',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      renderType: 'status',
      renderMeta: {
        colorMap: {
          ACTIVE: 'success',
          SUSPENDED: 'warning',
          TERMINATED: 'danger',
        } satisfies Record<AgentStatus, string>,
      },
    },
    {
      id: 'thisMonthCollectionAmount',
      header: 'Collections',
      accessor: 'thisMonthCollectionAmount',
      renderType: 'money',
      numeric: true,
      sortable: true,
    },
  ],
  filters: [
    { id: 'status', label: 'Status', type: 'select', options: AGENT_STATUS_OPTIONS },
  ],
  rowActions: [
    { label: 'Edit', onClick: () => {} },
    { label: 'Suspend', onClick: () => {}, variant: 'destructive', requiresConfirmation: true },
  ],
  onRowClick: (row) => window.open(`/agents/${row.id}`, '_self'),
  defaultPageSize: 20,
});

// ── Fetch function ──────────────────────────────────────────────────────────

const fetchAgents: PaginatedQueryFn<AgentListItem> = async (params) => {
  return agentClient.list(params as unknown as Record<string, string | number>);
};

// ── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/_auth/agents/')({
  component: AgentsIndexRoute,
});

function AgentsIndexRoute() {
  usePageTitle('Agents');

  const navigate = useNavigate();
  const search = useSearch({ strict: false });

  const urlAdapter = useMemo(
    () => createUrlAdapter(search, navigate as never),
    [search, navigate],
  );

  const list = useListPage(agentsConfig, fetchAgents, urlAdapter);

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Agents"
        subtitle="Manage field force and territory assignments."
        action={
          <Button asChild variant="primary">
            <Link to="/agents/new">
              New Agent
            </Link>
          </Button>
        }
      />

      <DataTable
        config={agentsConfig}
        list={list}
      />
    </div>
  );
}
